import { LegalTabs } from '../components/LegalTabs'
import { CONTACT_EMAIL, OPERATOR_NAME, TERMS_VERSION } from '../lib/constants'

export function PrivacyPage() {
  return (
    <section className="section legal-page">
      <div className="container narrow-container">
        <LegalTabs />
        <h1>Privacy notice</h1>
        <p className="legal-document-meta">Version {TERMS_VERSION}</p>
        <p className="legal-intro">
          This notice explains how personal data is processed through Rapid First Look,
          a free and independent service operated by {OPERATOR_NAME} in his personal
          capacity.
        </p>

        <div className="legal-content">
          <h2>Controller</h2>
          <p>
            The data controller is {OPERATOR_NAME}, acting as a natural person and
            independent operator of Rapid First Look. The service is not an official
            service of CNR or CNR-IMATI. Contact:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>

          <h2>Data processed</h2>
          <p>The platform may process:</p>
          <ul>
            <li>
              the mandatory email address, authentication data, account identifiers, and
              session records;
            </li>
            <li>
              optional first name, last name, affiliation, ORCID iD, Google Scholar profile,
              and research interests;
            </li>
            <li>
              for a private upload, the PDF and its technical metadata, including filename,
              file size, and storage path;
            </li>
            <li>
              for an arXiv-based submission, the public arXiv identifier and the fact that
              arXiv was selected as the manuscript source;
            </li>
            <li>
              the submission code, timestamps, status, source type, and the version of the
              terms accepted for the submission;
            </li>
            <li>the private feedback associated with the submission;</li>
            <li>security, access, email-delivery, and operational logs;</li>
            <li>
              communications concerning technical, privacy, security, or account issues.
            </li>
          </ul>
          <p>
            Passwords are handled by the authentication provider and are not available to
            the operator in readable form.
          </p>

          <h2>Purposes</h2>
          <p>Data is processed to:</p>
          <ul>
            <li>create, confirm, authenticate, and manage user accounts;</li>
            <li>receive and manage one active manuscript submission per account;</li>
            <li>maintain the anonymous public queue and aggregate service statistics;</li>
            <li>
              allow the operator to open a private PDF or access the public manuscript directly
              on arXiv and provide private feedback;
            </li>
            <li>preserve the private feedback for the duration of the active account;</li>
            <li>delete an uploaded source PDF when the process is completed or terminated;</li>
            <li>send transactional account and authentication emails;</li>
            <li>prevent abuse, malware, unauthorized access, and technical failures;</li>
            <li>comply with applicable legal obligations.</li>
          </ul>

          <h2>Legal bases</h2>
          <p>
            Processing required to create the account, manage a submission, and provide the
            requested feedback is based on the performance of the free service agreement with
            the user. Security, abuse prevention, service integrity, and limited aggregate
            operational statistics are based on the operator's legitimate interests, provided
            that they do not override the user's rights and freedoms. Processing required by
            law is based on the relevant legal obligation.
          </p>
          <p>
            Optional academic profile information is processed on the basis of the user's
            consent, expressed by voluntarily completing those fields. It can be removed at
            any time from the account without affecting access to the core service.
          </p>

          <h2>Public information</h2>
          <p>
            The public queue displays only an anonymous submission code, position, and general
            status. It does not display the user's email address, account profile, manuscript
            title, abstract, authors, affiliation, source type, arXiv identifier, uploaded PDF,
            or feedback.
          </p>

          <h2>Recipients and infrastructure</h2>
          <p>The service uses the following technical providers:</p>
          <ul>
            <li>Cloudflare, for website delivery, deployment, and security;</li>
            <li>
              Supabase, for authentication, database services, private PDF storage, and
              server-side functions;
            </li>
            <li>Brevo, for transactional authentication and account emails.</li>
          </ul>
          <p>
            These providers process data only as needed to supply their services and may use
            approved subprocessors. Their processing may involve locations outside the European
            Economic Area under the safeguards described in their applicable data-processing
            terms. Manuscript content and private feedback are not intentionally sent to Brevo.
          </p>
          <p>
            When an arXiv-based submission is used, the operator opens the public manuscript on
            the arXiv website. Rapid First Look stores only the identifier and does not upload or
            retain a manuscript copy. arXiv is an external service and its own terms and privacy
            information apply when its pages are accessed.
          </p>
          <p>
            No scientific content is intentionally shared with other users, advertising
            providers, data brokers, or generative AI providers.
          </p>

          <h2>Technical storage</h2>
          <p>
            The platform uses technical browser storage and similar mechanisms where necessary
            to maintain authentication sessions and protect access to private areas. It does not
            intentionally use advertising cookies or behavioral profiling tools.
          </p>

          <h2>Retention</h2>
          <ul>
            <li>
              Uploaded source PDF: until the private feedback or final private notice is
              published, or until the submission or account is withdrawn, closed, or deleted.
            </li>
            <li>
              arXiv identifier: while the submission remains associated with an active account.
              It is removed when a queued submission is withdrawn or when the account is closed.
            </li>
            <li>
              Private feedback and submission record: while the account remains active, unless
              the service ends or the user closes the account.
            </li>
            <li>
              Account email and optional profile: while the account remains active, subject to
              any strictly necessary legal or technical retention.
            </li>
            <li>
              Transactional email records, security logs, and provider backups: for the limited
              periods required for delivery, security, troubleshooting, and the providers'
              normal technical retention cycles.
            </li>
            <li>
              Anonymous aggregate completion statistics: may be retained without identifiers
              to report service activity.
            </li>
          </ul>
          <p>
            Closing an account removes active submissions, uploaded source PDFs, stored arXiv
            identifiers, private feedback, and account data from the active platform, subject to
            technical backup cycles and any retention required by law.
          </p>

          <h2>User rights</h2>
          <p>
            Subject to the conditions established by applicable law, users may request access,
            correction, deletion, restriction, or portability of their personal data, object to
            processing based on legitimate interests, and withdraw consent for optional profile
            information without affecting earlier lawful processing. Requests may be sent to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <p>
            Users may also lodge a complaint with the competent supervisory authority. For a
            controller established in Italy, this is normally the Garante per la protezione dei
            dati personali. Account closure is available from the private dashboard.
          </p>

          <h2>Sensitive and third-party information</h2>
          <p>
            Users must not upload unnecessary personal, clinical, confidential, credential,
            password, secret-key, or third-party data that they are not legally permitted to
            share. The user is responsible for ensuring that a private PDF can lawfully be
            uploaded and that an arXiv identifier refers to a work they are authorized to submit
            for this service. The operator may close the submission and delete any uploaded PDF.
          </p>

          <h2>Automated decision-making and AI</h2>
          <p>
            The service does not use automated scientific decision-making, profiling, or
            generative AI to read, summarize, assess, or draft feedback on the submitted work.
            Automated technical controls may be used for authentication, upload limits, queue
            calculations, file safety, identifier validation, and platform security.
          </p>

          <h2>Changes</h2>
          <p>
            Material changes to this notice will be identified by a new version. Where a change
            materially affects existing users or their data, reasonable notice will be provided
            through the platform or by email where appropriate.
          </p>
        </div>
      </div>
    </section>
  )
}
